// Tipos para Alertas Clinicos

import { RiskLevel } from './risk.types';

/**
 * Status de um alerta clinico
 */
export enum AlertStatus {
  PENDING = 'PENDING',           // Aguardando reconhecimento
  ACKNOWLEDGED = 'ACKNOWLEDGED', // Reconhecido pela equipe
  RESOLVED = 'RESOLVED',         // Resolvido
  DISMISSED = 'DISMISSED'        // Descartado (falso positivo)
}

/**
 * Tipos de alertas clinicos
 */
export enum AlertType {
  // Alertas de questionarios
  SUICIDAL_IDEATION = 'SUICIDAL_IDEATION',
  SEVERE_DEPRESSION = 'SEVERE_DEPRESSION',
  SEVERE_ANXIETY = 'SEVERE_ANXIETY',
  ALCOHOL_DEPENDENCE = 'ALCOHOL_DEPENDENCE',
  HIGH_CARDIOVASCULAR_RISK = 'HIGH_CARDIOVASCULAR_RISK',

  // Alertas de risco
  RISK_LEVEL_CRITICAL = 'RISK_LEVEL_CRITICAL',
  RISK_LEVEL_INCREASE = 'RISK_LEVEL_INCREASE',

  // Alertas clinicos
  ABNORMAL_VITAL_SIGNS = 'ABNORMAL_VITAL_SIGNS',
  MISSED_APPOINTMENT = 'MISSED_APPOINTMENT',
  MEDICATION_ADHERENCE = 'MEDICATION_ADHERENCE',

  // Alertas de linha de cuidado
  CARE_LINE_ALERT = 'CARE_LINE_ALERT',
  QUESTIONNAIRE_OVERDUE = 'QUESTIONNAIRE_OVERDUE'
}

/**
 * Resposta completa de um alerta
 */
export interface AlertResponse {
  id: string;
  patientId: string;
  patientName: string;
  type: AlertType;
  typeLabel: string;
  severity: RiskLevel;
  severityLabel: string;
  severityColor: string;
  title: string;
  message: string;
  sourceType?: string;
  sourceId?: string;
  status: AlertStatus;
  acknowledgedByName?: string;
  acknowledgedAt?: string;
  acknowledgedNotes?: string;
  resolvedByName?: string;
  resolvedAt?: string;
  resolutionNotes?: string;
  createdAt: string;
  urgent: boolean;
}

/**
 * Resumo de alertas para dashboard
 */
export interface AlertSummary {
  totalPending: number;
  urgentPending: number;
  nonUrgentPending: number;
}

/**
 * Request para reconhecer um alerta
 */
export interface AcknowledgeAlertRequest {
  notes?: string;
}

/**
 * Request para resolver um alerta
 */
export interface ResolveAlertRequest {
  resolutionNotes: string;
}

/**
 * Request para descartar um alerta
 */
export interface DismissAlertRequest {
  reason: string;
}

/**
 * Filtros para listagem de alertas
 */
export interface AlertFilters {
  status?: AlertStatus;
  patientId?: string;
  type?: AlertType;
  urgent?: boolean;
}

// Configuracoes de exibicao

export const AlertStatusLabels: Record<AlertStatus, string> = {
  [AlertStatus.PENDING]: 'Pendente',
  [AlertStatus.ACKNOWLEDGED]: 'Reconhecido',
  [AlertStatus.RESOLVED]: 'Resolvido',
  [AlertStatus.DISMISSED]: 'Descartado'
};

export const AlertStatusColors: Record<AlertStatus, {
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  [AlertStatus.PENDING]: {
    color: '#ca8a04',
    bgColor: '#fef9c3',
    borderColor: '#fde047'
  },
  [AlertStatus.ACKNOWLEDGED]: {
    color: '#2563eb',
    bgColor: '#dbeafe',
    borderColor: '#93c5fd'
  },
  [AlertStatus.RESOLVED]: {
    color: '#16a34a',
    bgColor: '#dcfce7',
    borderColor: '#86efac'
  },
  [AlertStatus.DISMISSED]: {
    color: '#6b7280',
    bgColor: '#f3f4f6',
    borderColor: '#d1d5db'
  }
};

export const AlertTypeLabels: Record<AlertType, string> = {
  [AlertType.SUICIDAL_IDEATION]: 'Ideação Suicida',
  [AlertType.SEVERE_DEPRESSION]: 'Depressão Grave',
  [AlertType.SEVERE_ANXIETY]: 'Ansiedade Grave',
  [AlertType.ALCOHOL_DEPENDENCE]: 'Dependência de Álcool',
  [AlertType.HIGH_CARDIOVASCULAR_RISK]: 'Alto Risco Cardiovascular',
  [AlertType.RISK_LEVEL_CRITICAL]: 'Risco Crítico',
  [AlertType.RISK_LEVEL_INCREASE]: 'Aumento de Risco',
  [AlertType.ABNORMAL_VITAL_SIGNS]: 'Sinais Vitais Anormais',
  [AlertType.MISSED_APPOINTMENT]: 'Consulta Perdida',
  [AlertType.MEDICATION_ADHERENCE]: 'Adesão a Medicação',
  [AlertType.CARE_LINE_ALERT]: 'Alerta de Linha de Cuidado',
  [AlertType.QUESTIONNAIRE_OVERDUE]: 'Questionário Atrasado'
};

/**
 * Tipos de alerta considerados urgentes
 */
export const UrgentAlertTypes: AlertType[] = [
  AlertType.SUICIDAL_IDEATION,
  AlertType.SEVERE_DEPRESSION,
  AlertType.SEVERE_ANXIETY,
  AlertType.ALCOHOL_DEPENDENCE,
  AlertType.RISK_LEVEL_CRITICAL,
  AlertType.ABNORMAL_VITAL_SIGNS
];

/**
 * Verifica se um tipo de alerta e urgente
 */
export function isUrgentAlertType(type: AlertType): boolean {
  return UrgentAlertTypes.includes(type);
}
